import type { Ride } from "../api/types";
import { decodeStravaPolyline } from "../lib/polyline";

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;

function buildMapboxUrl(polyline: string): string | null {
  if (!MAPBOX_TOKEN || !polyline) return null;
  const encoded = encodeURIComponent(polyline);
  if (encoded.length > 6000) return null; // avoid URL length limit
  const overlay = `path-4+D76339-0.9(${encoded})`;
  return `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/${overlay}/auto/320x180@2x?access_token=${MAPBOX_TOKEN}&padding=24,24,24,24`;
}

export function RideCard({ ride }: { ride: Ride }) {
  const mapboxUrl = ride.polyline ? buildMapboxUrl(ride.polyline) : null;
  const map = !mapboxUrl && ride.polyline ? buildMap(ride.polyline) : null;
  const date = ride.dateIso
    ? new Date(ride.dateIso).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";

  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--line)",
        borderRadius: 16,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Map */}
      <div style={{ background: "var(--map-bg)", aspectRatio: "16/9", position: "relative", overflow: "hidden" }}>
        {mapboxUrl ? (
          <img
            src={mapboxUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : map ? (
          <svg
            viewBox={`0 0 ${map.w} ${map.h}`}
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: "block" }}
          >
            <path
              d={map.path}
              stroke="var(--accent)"
              strokeWidth={2.2}
              strokeOpacity={0.75}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx={map.start[0]} cy={map.start[1]} r={4} fill="var(--accent)" opacity={0.9} />
            <circle cx={map.start[0]} cy={map.start[1]} r={7} fill="var(--accent)" opacity={0.2} />
            <circle cx={map.end[0]} cy={map.end[1]} r={3.5} fill="white" stroke="var(--accent)" strokeWidth={1.5} opacity={0.9} />
          </svg>
        ) : null}
      </div>

      {/* Info */}
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={{ fontSize: 11, color: "var(--ink-soft)", fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase" }}>
            {date}
          </div>
          {ride.location && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--ink-soft)" }}>
              <svg width="9" height="11" viewBox="0 0 10 12" fill="none">
                <path d="M5 0C2.79 0 1 1.79 1 4c0 2.5 4 8 4 8s4-5.5 4-8c0-2.21-1.79-4-4-4zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" fill="currentColor" opacity=".55" />
              </svg>
              {ride.location}
            </div>
          )}
        </div>
        <div style={{ fontWeight: 600, fontSize: 15, marginTop: 3 }}>{ride.name || "Untitled ride"}</div>
        <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
          <StatPill value={`${ride.miles.toFixed(1)} mi`} />
          <StatPill
            value={
              ride.timeMin >= 60
                ? `${Math.floor(ride.timeMin / 60)}h ${Math.round(ride.timeMin % 60)}m`
                : `${Math.round(ride.timeMin)}m`
            }
          />
          <StatPill value={`${Math.round(ride.elevFt)} ft`} />
          {ride.paceMph > 0 && <StatPill value={`${ride.paceMph.toFixed(1)} mph`} accent />}
        </div>
      </div>
    </div>
  );
}

function StatPill({ value, accent }: { value: string; accent?: boolean }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        background: accent ? "var(--accent-tint)" : "var(--bg)",
        border: `1px solid ${accent ? "transparent" : "var(--line)"}`,
        color: accent ? "var(--accent)" : "var(--ink)",
        fontWeight: 600,
        fontSize: 12,
      }}
    >
      {value}
    </span>
  );
}

interface MapData {
  path: string;
  start: [number, number];
  end: [number, number];
  w: number;
  h: number;
}

function buildMap(encoded: string): MapData | null {
  const pts = decodeStravaPolyline(encoded);
  if (pts.length < 2) return null;

  const w = 320;
  const h = 180;
  const padding = 16;

  const lats = pts.map((p) => p[0]);
  const lngs = pts.map((p) => p[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const dx = maxLng - minLng || 0.001;
  const dy = maxLat - minLat || 0.001;
  const scale = Math.min((w - padding * 2) / dx, (h - padding * 2) / dy);
  const offsetX = (w - dx * scale) / 2;
  const offsetY = (h - dy * scale) / 2;

  const toXY = (p: [number, number]): [number, number] => [
    offsetX + (p[1] - minLng) * scale,
    h - (offsetY + (p[0] - minLat) * scale),
  ];

  const path = pts
    .map((p, i) => {
      const [x, y] = toXY(p);
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  return {
    path,
    start: toXY(pts[0]),
    end: toXY(pts[pts.length - 1]),
    w,
    h,
  };
}
